import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";

const pluginKey = new PluginKey("autoLangSpellcheck");

const HEBREW_RE = /[\u0590-\u05FF]/g;
const LATIN_RE = /[A-Za-z]/g;

function countMatches(re, text) {
  const m = text.match(re);
  return m ? m.length : 0;
}

/**
 * Detect spellcheck lang + natural script direction from block text.
 * @param {string} text
 * @returns {{ lang: string, dir: "ltr" | "rtl" } | null}
 */
export function detectLangAndDir(text) {
  const s = String(text || "").trim();
  if (!s) return null;

  const heb = countMatches(HEBREW_RE, s);
  const lat = countMatches(LATIN_RE, s);

  if (heb === 0 && lat === 0) return null;

  if (heb > 0 && lat === 0) {
    return { lang: "he", dir: "rtl" };
  }

  const total = heb + lat;
  const hebRatio = total ? heb / total : 0;

  if (heb >= 2 && hebRatio >= 0.55) {
    return { lang: "he", dir: "rtl" };
  }

  return { lang: "en", dir: "ltr" };
}

/**
 * In RTL documents, keep block layout RTL even for Latin-only headings
 * (e.g. T12-L1). Still set lang for spellcheck.
 *
 * @param {{ lang: string, dir: "ltr" | "rtl" } | null} detected
 * @param {"ltr" | "rtl"} documentDir
 * @returns {{ lang: string, dir: "ltr" | "rtl" } | null}
 */
export function resolveBlockLangDir(detected, documentDir) {
  if (!detected) return null;
  if (documentDir === "rtl") {
    return { lang: detected.lang, dir: "rtl" };
  }
  return detected;
}

function computeChangedRanges(transaction) {
  const ranges = [];
  for (const map of transaction.mapping.maps) {
    map.forEach((from, to, newFrom, newTo) => {
      ranges.push({ from: newFrom, to: newTo });
    });
  }
  return ranges.length ? ranges : null;
}

function getDocumentDir(editorView) {
  return editorView?.dom?.getAttribute("dir") === "rtl" ? "rtl" : "ltr";
}

export const AutoLangSpellcheck = Extension.create({
  name: "autoLangSpellcheck",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading", "listItem"],
        attributes: {
          lang: {
            default: null,
            parseHTML: (element) => element.getAttribute("lang") || null,
            renderHTML: (attrs) => (attrs.lang ? { lang: attrs.lang } : {}),
          },
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute("dir") || null,
            renderHTML: (attrs) => (attrs.dir ? { dir: attrs.dir } : {}),
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const extensionThis = this;

    return [
      new Plugin({
        key: pluginKey,

        state: {
          init() {
            return { pending: [], timer: null, lastDocumentDir: null };
          },
          apply(tr, value) {
            if (tr.getMeta(pluginKey) || tr.getMeta("autoLangSpellcheck")) {
              return value;
            }

            if (!tr.docChanged) return value;

            const next = { ...value };
            const ranges = computeChangedRanges(tr);
            if (ranges) next.pending = next.pending.concat(ranges);
            return next;
          },
        },

        view(editorView) {
          let destroyed = false;

          const schedule = (forceFullDoc = false) => {
            if (destroyed) return;
            const state = pluginKey.getState(editorView.state);
            if (!state) return;

            if (forceFullDoc) {
              state.pending = [
                { from: 0, to: editorView.state.doc.content.size },
              ];
            }
            if (!state.pending?.length) return;

            if (state.timer) return;

            state.timer = window.setTimeout(() => {
              try {
                const current = pluginKey.getState(editorView.state);
                if (!current?.pending?.length) return;

                const pending = current.pending;
                current.pending = [];
                if (current.timer) {
                  clearTimeout(current.timer);
                  current.timer = null;
                }

                const doc = editorView.state.doc;
                const documentDir = getDocumentDir(editorView);
                const toProcess = [];

                for (const r of pending) {
                  const from = Math.max(0, Math.min(r.from, doc.content.size));
                  const to = Math.max(0, Math.min(r.to, doc.content.size));
                  toProcess.push({
                    from: Math.min(from, to),
                    to: Math.max(from, to),
                  });
                }

                const blocks = new Map();
                for (const { from, to } of toProcess) {
                  doc.nodesBetween(from, to, (node, pos) => {
                    if (
                      node.type.name === "paragraph" ||
                      node.type.name === "heading" ||
                      node.type.name === "listItem"
                    ) {
                      blocks.set(pos, node);
                      return false;
                    }
                    return true;
                  });
                }

                if (!blocks.size) return;

                let tr = editorView.state.tr;
                tr.setMeta(pluginKey, true);
                tr.setMeta("autoLangSpellcheck", true);

                for (const [pos, node] of blocks.entries()) {
                  const text = node.textContent || "";
                  const resolved = resolveBlockLangDir(
                    detectLangAndDir(text),
                    documentDir,
                  );
                  if (!resolved) continue;

                  const nextAttrs = {
                    ...node.attrs,
                    lang: resolved.lang,
                    dir: resolved.dir,
                  };

                  // In RTL reports, drop explicit left align so Latin labels
                  // follow the document edge (right).
                  if (
                    documentDir === "rtl" &&
                    nextAttrs.textAlign === "left"
                  ) {
                    nextAttrs.textAlign = "right";
                  }

                  if (
                    node.attrs.lang === nextAttrs.lang &&
                    node.attrs.dir === nextAttrs.dir &&
                    node.attrs.textAlign === nextAttrs.textAlign
                  ) {
                    continue;
                  }

                  tr = tr.setNodeMarkup(pos, undefined, nextAttrs, node.marks);
                }

                if (tr.docChanged) {
                  editorView.dispatch(tr);
                }
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn("[AutoLangSpellcheck] update failed:", e);
              } finally {
                const current = pluginKey.getState(editorView.state);
                if (current?.timer) {
                  clearTimeout(current.timer);
                  current.timer = null;
                }
              }
            }, extensionThis.options?.debounceMs ?? 400);
          };

          return {
            update(view) {
              const state = pluginKey.getState(view.state);
              const documentDir = getDocumentDir(view);
              const dirChanged =
                state &&
                state.lastDocumentDir != null &&
                state.lastDocumentDir !== documentDir;
              if (state) state.lastDocumentDir = documentDir;
              schedule(Boolean(dirChanged));
            },
            destroy() {
              destroyed = true;
              const state = pluginKey.getState(editorView.state);
              if (state?.timer) {
                clearTimeout(state.timer);
                state.timer = null;
              }
            },
          };
        },
      }),
    ];
  },
});

export default AutoLangSpellcheck;
